import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useKleverWallet } from '../../context/KleverWalletContext';
import { AuthCard, Input, Button } from '../../components';
import api from '../../utils/api';
import { FaWallet } from 'react-icons/fa';
import './Signup.scss';

/** Read the `from` field from location state, accepting both a bare
 *  string (ADR-007 producers) and a `{ pathname }` object (older
 *  ProtectedRoute pattern). Returns null if neither shape matches, so
 *  the caller can fall back to a default. */
function readFromLocationState(state: unknown): string | null {
  const raw = (state as any)?.from;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && typeof raw.pathname === 'string') {
    return raw.pathname + (raw.search || '');
  }
  return null;
}

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
  met: boolean;
}

/**
 * Signup page component
 * Handles new user registration
 */
const Signup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // ADR-007 signup-guard bounce-back: the `from` was set by
  // useSignupGuard / the anon AppBar buttons when the visitor tried
  // to interact on a public content page. After a successful wallet
  // signup we send them back to that page; email signup goes to
  // verification-inbox first, so the bounce-back happens after
  // /verify-email → dashboard.
  const from = readFromLocationState(location.state);
  const { register, walletLogin, isLoading, error, isAuthenticated } = useAuth();
  const { connectWallet, signMessage, isConnecting, isExtensionInstalled, error: walletError } = useKleverWallet();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirement[]>([
    { label: 'At least 8 characters', test: (pwd) => pwd.length >= 8, met: false },
    { label: 'Contains uppercase letter', test: (pwd) => /[A-Z]/.test(pwd), met: false },
    { label: 'Contains lowercase letter', test: (pwd) => /[a-z]/.test(pwd), met: false },
    { label: 'Contains number', test: (pwd) => /[0-9]/.test(pwd), met: false },
    { label: 'Contains special character', test: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd), met: false },
  ]);

  // Update password requirements as user types
  useEffect(() => {
    setPasswordRequirements((prev) =>
      prev.map((req) => ({
        ...req,
        met: req.test(password),
      }))
    );
  }, [password]);

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  // Check if all password requirements are met
  const allRequirementsMet = passwordRequirements.every((req) => req.met);
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLocalError(null);

    // Validation
    if (!email || !password || !confirmPassword) {
      setLocalError('All required fields must be filled');
      return;
    }

    if (!allRequirementsMet) {
      setLocalError('Please meet all password requirements');
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    try {
      const result = await register(email, password);

      if (result.meta.requestStatus === 'fulfilled') {
        // Show success message instead of redirecting to dashboard
        setSuccess(true);
      }
    } catch (err) {
      console.error('Registration failed:', err);
    }
  };

  const handleWalletSignup = async () => {
    try {
      const walletAddress = await connectWallet();

      // Server-issued challenge (auth-audit F-2 + F-5 fix). Same shape
      // as the login page — see app/login/page.tsx for the rationale.
      const { data: challenge } = await api.post<{ message: string }>(
        '/auth/wallet-challenge',
        { walletAddress },
      );
      const signature = await signMessage(challenge.message);

      const result = await walletLogin(walletAddress, signature, challenge.message);
      if (result.meta.requestStatus === 'fulfilled') {
        const userData = (result.payload as any)?.user;
        // No profile yet → onboarding always wins. Otherwise, honour
        // `from` (set by ADR-007 signup-guard) or fall back to dashboard.
        const dest = userData?.hasProfile === false
          ? '/create-profile'
          : (from || '/dashboard');
        navigate(dest, { replace: true });
      }
    } catch (err) {
      console.error('[Signup] Wallet signup failed:', err);
    }
  };

  const displayError = localError || error;

  if (success) {
    return (
      <AuthCard
        title="Check Your Email!"
        subtitle="We've sent you a verification link"
        footerText="Didn't receive the email?"
        footerLink={{ text: 'Contact support', href: 'mailto:support@klyx.space' }}
      >
        <div className="success-container">
          <div className="success-icon">✉️</div>
          <h3>Thank you for signing up!</h3>
          <p className="success-message">
            Please check your email inbox for a verification link.
            You'll need to verify your email before you can log in.
          </p>
          <p className="success-hint">
            The verification link will expire in 24 hours.
          </p>
          <Button
            variant="primary"
            size="large"
            fullWidth
            onClick={() => navigate('/login')}
          >
            Go to Login
          </Button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Join Klyx"
      subtitle="Create your account and start trading"
      footerText="Already have an account?"
      footerLink={{ text: 'Log in', to: '/login' }}
    >
      {/* Wallet signup — top of form for visibility */}
      <div className="wallet-signup-section">
        <div className="wallet-signup-header">
          <FaWallet className="wallet-signup-icon" />
          <div>
            <h3>Have a Klever Wallet?</h3>
            <p>Connect it to instantly sign up or log in — no email needed.</p>
          </div>
        </div>

        {walletError && <div className="error-banner">{walletError}</div>}

        {!isExtensionInstalled ? (
          <p className="wallet-signup-install">
            <a
              href="https://chrome.google.com/webstore/detail/klever-wallet/ifclboecfhkjbpmhgehodcjpciihhmif"
              target="_blank"
              rel="noopener noreferrer"
            >
              Install the Klever Extension
            </a>
            {' '}or use the Klever Mobile Browser to connect your wallet.
          </p>
        ) : (
          <Button
            variant="secondary"
            size="large"
            fullWidth
            isLoading={isConnecting}
            onClick={handleWalletSignup}
          >
            {isConnecting ? 'Connecting...' : 'Continue with Klever Wallet'}
          </Button>
        )}

        <p className="wallet-signup-bonus">
          New wallet accounts receive a welcome bonus of 30 KLV automatically.
        </p>
      </div>

      <div className="signup-divider">
        <span>or sign up with email</span>
      </div>

      <form onSubmit={handleSubmit} className="signup-form">
        {displayError && <div className="error-banner">{displayError}</div>}

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          disabled={isLoading}
          autoComplete="email"
        />

        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Create a strong password"
          required
          disabled={isLoading}
          autoComplete="new-password"
        />

        {/* Password Requirements */}
        {password && (
          <div className="password-requirements">
            <p className="requirements-title">Password must contain:</p>
            <ul className="requirements-list">
              {passwordRequirements.map((req, index) => (
                <li key={index} className={`requirement ${req.met ? 'met' : 'unmet'}`}>
                  <span className="requirement-icon">{req.met ? '✓' : '○'}</span>
                  <span className="requirement-text">{req.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Input
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your password"
          required
          disabled={isLoading}
          autoComplete="new-password"
        />

        {/* Password Match Indicator */}
        {confirmPassword && (
          <div className={`password-match ${passwordsMatch ? 'match' : 'no-match'}`}>
            <span className="match-icon">{passwordsMatch ? '✓' : '✗'}</span>
            <span className="match-text">
              {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
            </span>
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="large"
          fullWidth
          isLoading={isLoading}
          disabled={!email || !password || !confirmPassword || !allRequirementsMet || !passwordsMatch}
        >
          {isLoading ? 'Creating account...' : 'Continue'}
        </Button>
      </form>
    </AuthCard>
  );
};

export default Signup;
